import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import dayjs from "dayjs";
import { SERVER_URL } from "../../../constants";
import { addStudentStay } from "../../shared utils/apiUtils.js";
import BespokeEventDialog from "../../shared utils/BespokeEventDialog.jsx";
import StudentCopyBookingsDialog from "./StudentCopyBookingsDialog.jsx";
import {
  d10,
  maxStr,
  minStr,
  normCampus,
  TYPE_META,
  LEGEND_ORDER,
  typeMeta,
  typeRank,
  anchorDay,
  eachDay,
  packLanes,
  packLanesAdjacent,
  planSimpleLaneCells,
  planResLaneCells,
} from "../../shared utils/bookingsGrid.js";

const pid = (p) => p.productId ?? p.id;

async function fetchStudentBookings(studentId, token) {
  const res = await fetch(`${SERVER_URL}api/student/${studentId}/booking`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 204) return [];
  if (!res.ok) throw new Error(`bookings ${studentId} -> ${res.status}`);
  return res.json();
}

// A stay/booking belongs to the selected year if it overlaps that calendar year.
const inYear = (year, start, end) => {
  if (!year) return true;
  return d10(start) <= `${year}-12-31` && `${year}-01-01` <= d10(end);
};

const toBlock = (raw) => ({
  id: raw.bookingId,
  productId: raw.productId,
  productName: raw.productName,
  productType: raw.productType,
  campus: raw.campus ?? raw.base ?? "",
  actualCharge: raw.actualCharge,
  start: d10(raw.startDate),
  end: d10(raw.endDate),
  raw,
});

// Individual-student version of the day-grid bookings view. Unlike the group view, the
// columns span the student's FULL stay duration (plus any bookings), products come from
// a per-stay wide search, and conflicts are judged against the student's own stays:
// grey = a booking outside all their stays, red = a booking on a campus they don't stay
// at. Same colours, half-cell residential weeks, edit/delete and add-dropdown as the group.
export default function StudentBookingsGrid({ studentId, token, showFinancials = true, selectedYear, onChanged }) {
  const [bookings, setBookings] = useState([]);
  const [stays, setStays] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [bespokeOpen, setBespokeOpen] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [stayDialog, setStayDialog] = useState(null); // { stayId, campus, arrivalDate, departureDate, residential }
  const [staySaving, setStaySaving] = useState(false);

  useEffect(() => {
    fetch(`${SERVER_URL}api/campuses/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCampuses(Array.isArray(d) ? d : []))
      .catch(() => setCampuses([]));
  }, [token]);

  const reload = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [bk, stayList] = await Promise.all([
        fetchStudentBookings(studentId, token).catch(() => []),
        fetch(`${SERVER_URL}api/student/${studentId}/stays`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => (r.status === 204 ? [] : r.ok ? r.json() : []))
          .catch(() => []),
      ]);
      // Scope the whole view to the selected year — all of the student's stays and
      // bookings that fall in that year (across every group/stay), nothing from others.
      setBookings((Array.isArray(bk) ? bk : []).filter((b) => inYear(selectedYear, b.startDate, b.endDate)));
      const staysAll = Array.isArray(stayList) ? stayList : stayList ? [stayList] : [];
      const staysArr = staysAll.filter((s) => inYear(selectedYear, s.arrivalDate, s.departureDate));
      setStays(staysArr);

      // Per-stay wide product search (no group here), unioned by product id.
      const byId = new Map();
      const ids = staysArr.map((s) => s.stayId).filter(Boolean);
      for (let i = 0; i < ids.length; i += 6) {
        const chunk = ids.slice(i, i + 6);
        const results = await Promise.all(
          chunk.map((sid) =>
            fetch(`${SERVER_URL}api/products/eligableProductsWide/stay/${sid}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => [])
          )
        );
        results.forEach((e) => {
          (e?.products || e || []).forEach((p) => {
            const key = String(p.productId ?? p.id);
            if (!byId.has(key)) byId.set(key, p);
          });
        });
      }
      const list = Array.from(byId.values());
      list.sort((a, b) =>
        String(a.productName || a.name).localeCompare(String(b.productName || b.name))
      );
      setProducts(list);
    } catch (e) {
      // best-effort; leave whatever loaded
    } finally {
      setLoading(false);
    }
  }, [studentId, token, selectedYear]);

  useEffect(() => {
    reload();
  }, [reload]);

  const refresh = async () => {
    const bk = await fetchStudentBookings(studentId, token).catch(() => null);
    if (bk != null) {
      setBookings((Array.isArray(bk) ? bk : []).filter((b) => inYear(selectedYear, b.startDate, b.endDate)));
    }
    onChanged?.();
  };

  const stayRanges = useMemo(
    () =>
      stays
        .map((s) => {
          // Guard against stays saved with arrival/departure reversed — treat as min/max
          // so the grid still renders rather than collapsing to an empty day range.
          const a = d10(s.arrivalDate);
          const b = d10(s.departureDate);
          const start = a && b ? minStr(a, b) : a || b;
          const end = a && b ? maxStr(a, b) : a || b;
          return { start, end, campus: s.campus || "" };
        })
        .filter((s) => s.start && s.end),
    [stays]
  );
  const stayCampuses = useMemo(
    () => new Set(stayRanges.map((s) => normCampus(s.campus)).filter(Boolean)),
    [stayRanges]
  );
  const range = useMemo(() => {
    if (!stayRanges.length) return null;
    const a = stayRanges.map((s) => s.start).sort()[0];
    const b = stayRanges.map((s) => s.end).sort().slice(-1)[0];
    return a && b ? { start: a, end: b } : null;
  }, [stayRanges]);

  const blocks = useMemo(
    () => bookings.map(toBlock).filter((b) => b.start && b.end),
    [bookings]
  );

  // Columns = the full span of the student's stays AND their bookings.
  const days = useMemo(() => {
    let min = null;
    let max = null;
    [...stayRanges, ...blocks].forEach((r) => {
      if (r.start && (!min || r.start < min)) min = r.start;
      if (r.end && (!max || r.end > max)) max = r.end;
    });
    return min && max ? eachDay(min, max) : [];
  }, [stayRanges, blocks]);

  const overlapsAnyStay = (b) =>
    stayRanges.some((s) => b.start <= s.end && s.start <= b.end);

  const blockStyle = (b) => {
    const bCampus = b.campus || "";
    const outside = stayRanges.length > 0 && !overlapsAnyStay(b);
    const wrongCampus = bCampus && stayCampuses.size > 0 && !stayCampuses.has(normCampus(bCampus));
    const tm = typeMeta(b.productType);
    if (outside) {
      return {
        bg: "#eeeeee",
        fg: "#757575",
        tip: "Outside the student's stays",
      };
    }
    if (wrongCampus) {
      return {
        bg: "#ffcdd2",
        fg: "#b71c1c",
        tip: `Wrong campus: ${bCampus} — student stays at ${Array.from(
          new Set(stayRanges.map((s) => s.campus).filter(Boolean))
        ).join(", ") || "?"}`,
      };
    }
    return { bg: tm.color, fg: tm.border, tip: `${tm.label}${bCampus ? ` · ${bCampus}` : ""}` };
  };

  const bookOne = async (product) => {
    if (!range) return;
    setBusy(true);
    try {
      const start = maxStr(d10(product.startDate) || range.start, range.start);
      const end = minStr(d10(product.endDate) || range.end, range.end);
      const res = await fetch(
        `${SERVER_URL}api/bookings/studentId/${studentId}/productId/${pid(product)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            actualCharge: Number(product.defaultRate) || 0,
            bookingStatus: "PROVISIONAL",
            customStart: start,
            customEnd: end,
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (e) {
      // ignore; UI stays
    } finally {
      setBusy(false);
    }
  };

  // Book this student onto a freshly-created bespoke EVENT product (via the dialog).
  const bookBespoke = async ({ productId, perHead }) => {
    const res = await fetch(
      `${SERVER_URL}api/bookings/studentId/${studentId}/productId/${productId}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ actualCharge: perHead, bookingStatus: "PROVISIONAL" }),
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Failed to book the event (${res.status})`);
    }
    await refresh();
  };

  const deleteBooking = async (block) => {
    const prev = bookings;
    setBookings((bs) => bs.filter((x) => x.bookingId !== block.id));
    try {
      const res = await fetch(`${SERVER_URL}api/bookings/${block.id}/product/${block.productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onChanged?.();
    } catch (e) {
      setBookings(prev);
    }
  };

  const saveEdit = async () => {
    if (!editBooking) return;
    setBusy(true);
    try {
      const res = await fetch(`${SERVER_URL}api/bookings/${editBooking.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...editBooking._raw,
          actualCharge: Number(editBooking.actualCharge) || 0,
          customStart: editBooking.start,
          customEnd: editBooking.end,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditBooking(null);
      await refresh();
    } catch (e) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const dispBlocks = useMemo(
    () =>
      days.length
        ? blocks
            .map((b) => ({
              ...b,
              dispStart: maxStr(b.start, days[0]),
              dispEnd: minStr(b.end, days[days.length - 1]),
            }))
            .filter((b) => b.dispStart <= b.dispEnd)
        : [],
    [blocks, days]
  );

  const lanes = useMemo(
    () => [
      ...packLanesAdjacent(dispBlocks.filter((b) => typeRank(b.productType) === 0)).map((lane) => ({
        lane,
        isRes: true,
      })),
      ...packLanes(dispBlocks.filter((b) => typeRank(b.productType) !== 0)).map((lane) => ({
        lane,
        isRes: false,
      })),
    ],
    [dispBlocks]
  );

  // --- Stays on the grid (create/edit/delete, wired to the stay endpoints) --------
  const campusNames = useMemo(
    () => Array.from(new Set(campuses.map((c) => c.name || c.campusName).filter(Boolean))),
    [campuses]
  );

  const stayBlocks = useMemo(() => {
    if (!days.length) return [];
    return stays
      .map((s) => {
        const a = d10(s.arrivalDate);
        const b = d10(s.departureDate);
        const start = a && b ? minStr(a, b) : a || b;
        const end = a && b ? maxStr(a, b) : a || b;
        return { stayId: s.stayId, campus: s.campus || "", residential: s.residential, start, end, raw: s };
      })
      .filter((s) => s.start && s.end)
      .map((s) => ({
        ...s,
        dispStart: maxStr(s.start, days[0]),
        dispEnd: minStr(s.end, days[days.length - 1]),
      }))
      .filter((s) => s.dispStart <= s.dispEnd);
  }, [stays, days]);

  const stayLanes = useMemo(() => packLanes(stayBlocks), [stayBlocks]);

  const openCreateStay = () =>
    setStayDialog({ stayId: null, campus: campusNames[0] || "", arrivalDate: "", departureDate: "", residential: true });
  const openEditStay = (s) =>
    setStayDialog({
      stayId: s.stayId,
      campus: s.campus || "",
      arrivalDate: s.start,
      departureDate: s.end,
      residential: s.residential ?? true,
      raw: s.raw,
    });

  const saveStay = async () => {
    if (!stayDialog || staySaving) return;
    const { stayId, campus, arrivalDate, departureDate, residential } = stayDialog;
    if (!campus || !arrivalDate || !departureDate) return;
    setStaySaving(true);
    try {
      if (stayId) {
        const res = await fetch(`${SERVER_URL}api/student/${studentId}/stay/${stayId}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ ...(stayDialog.raw || {}), campus, arrivalDate, departureDate, residential }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        await addStudentStay({ campus, arrivalDate, departureDate, residential }, studentId);
      }
      setStayDialog(null);
      await reload();
    } catch (e) {
      window.alert("Failed to save stay");
    } finally {
      setStaySaving(false);
    }
  };

  const removeStay = async (s) => {
    if (!window.confirm("Delete this stay?")) return;
    try {
      const res = await fetch(`${SERVER_URL}api/student/${studentId}/stay/${s.stayId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
    } catch (e) {
      window.alert("Failed to delete stay");
    }
  };

  const STAY_STYLE = { bg: "#eceff1", border: "1px dashed #607d8b", fg: "#455a64" };
  const renderStayCells = (lane, laneKey) =>
    planSimpleLaneCells(lane, days).map((c, i) => {
      const key = `${laneKey}-${i}`;
      if (c.type === "empty") return <TableCell key={key} />;
      const s = c.block;
      return (
        <TableCell
          key={key}
          colSpan={c.span}
          align="center"
          title={`Stay · ${s.campus || "—"} (${s.start} → ${s.end})`}
          sx={{ bgcolor: STAY_STYLE.bg, border: STAY_STYLE.border, borderRadius: 1, py: 1 }}
        >
          <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
            <Typography variant="caption" sx={{ fontWeight: 600, color: STAY_STYLE.fg }}>
              {s.campus || "Stay"}
            </Typography>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => openEditStay(s)}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" color="error" sx={{ p: 0.25 }} onClick={() => removeStay(s)}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </TableCell>
      );
    });

  const blockInner = (b, st) => (
    <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
      <Stack spacing={0} alignItems="center">
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: st.fg, whiteSpace: "normal", lineHeight: 1.15, wordBreak: "keep-all", overflowWrap: "normal" }}
        >
          {b.productName || "Product"}
        </Typography>
        {b.campus ? (
          <Typography variant="caption" sx={{ color: st.fg, opacity: 0.7, lineHeight: 1.1, fontSize: 10 }}>
            {b.campus}
          </Typography>
        ) : null}
      </Stack>
      <IconButton
        size="small"
        sx={{ p: 0.25 }}
        onClick={() =>
          setEditBooking({
            id: b.id,
            productName: b.productName,
            actualCharge: b.actualCharge,
            start: b.start,
            end: b.end,
            _raw: b.raw,
          })
        }
      >
        <EditIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton size="small" color="error" sx={{ p: 0.25 }} onClick={() => deleteBooking(b)}>
        <DeleteIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Stack>
  );

  const renderCells = (descriptors, laneKey) =>
    descriptors.map((c, i) => {
      const key = `${laneKey}-${i}`;
      if (c.type === "empty") return <TableCell key={key} />;
      const st = blockStyle(c.block);
      if (c.type === "block") {
        return (
          <TableCell
            key={key}
            colSpan={c.span}
            align="center"
            title={st.tip}
            sx={{ bgcolor: st.bg, border: `1px solid ${st.fg}`, borderRadius: 1, py: 2.5 }}
          >
            {blockInner(c.block, st)}
          </TableCell>
        );
      }
      if (c.type === "body") {
        return (
          <TableCell key={key} colSpan={c.span} align="center" title={st.tip} sx={{ bgcolor: st.bg, py: 2.5 }}>
            {blockInner(c.block, st)}
          </TableCell>
        );
      }
      if (c.type === "startHalf") {
        return (
          <TableCell
            key={key}
            title={st.tip}
            sx={{ p: 0, background: `linear-gradient(to right, transparent 50%, ${st.bg} 50%)` }}
          />
        );
      }
      if (c.type === "endHalf") {
        return (
          <TableCell
            key={key}
            title={st.tip}
            sx={{ p: 0, background: `linear-gradient(to right, ${st.bg} 50%, transparent 50%)` }}
          />
        );
      }
      // boundary
      const nextSt = blockStyle(c.nextBlock);
      return (
        <TableCell
          key={key}
          title={`${st.tip} → ${nextSt.tip}`}
          sx={{ p: 0, background: `linear-gradient(to right, ${st.bg} 50%, ${nextSt.bg} 50%)` }}
        />
      );
    });

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading bookings…</Typography>
      </Stack>
    );
  }

  const laneRows = lanes.length ? lanes : [{ lane: [], isRes: false }];
  const stayLaneRows = stayLanes.length ? stayLanes : [[]];

  return (
    <Box sx={{ p: 2, m: "0 auto", width: "90%", bgcolor: "#fff", borderRadius: 1 }}>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreateStay}>
          Add stay
        </Button>
        {showFinancials && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setBespokeOpen(true)}
          >
            Add bespoke event
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={() => setCopyOpen(true)}
        >
          Copy from
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          The student&apos;s bookings across their full stay. Blocks span the days they cover;
          residential weeks chain on one line via half-cells. Trash/edit a block, or pick from
          the dropdown to add a product (clamped to the stay). Grey = a booking outside the
          student&apos;s stays; red = a campus they don&apos;t stay at.
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}>
          {LEGEND_ORDER.map((k) => (
            <Stack key={k} direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: TYPE_META[k].color,
                  border: `1px solid ${TYPE_META[k].border}`,
                }}
              />
              <Typography variant="caption">{TYPE_META[k].label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {days.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No stays or bookings to show.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ "& td, & th": { borderRight: "1px solid #eee", px: 0.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#fff", minWidth: 120 }}>
                  Day
                </TableCell>
                {days.map((day) => (
                  <TableCell key={day} align="center" sx={{ minWidth: 56 }}>
                    <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                      {dayjs(day).format(" dd D")}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stayLaneRows.map((lane, li) => (
                <TableRow key={`stay-${li}`}>
                  {li === 0 && (
                    <TableCell
                      rowSpan={stayLaneRows.length}
                      sx={{ position: "sticky", left: 0, bgcolor: "#fff", verticalAlign: "top", fontWeight: 600 }}
                    >
                      Stays
                    </TableCell>
                  )}
                  {renderStayCells(lane, `stay-${li}`)}
                </TableRow>
              ))}
              {laneRows.map(({ lane, isRes }, li) => (
                <TableRow key={`lane-${li}`}>
                  {li === 0 && (
                    <TableCell
                      rowSpan={laneRows.length + 1}
                      sx={{ position: "sticky", left: 0, bgcolor: "#fff", verticalAlign: "top", fontWeight: 600 }}
                    >
                      Bookings
                    </TableCell>
                  )}
                  {renderCells(
                    isRes ? planResLaneCells(lane, days) : planSimpleLaneCells(lane, days),
                    `lane-${li}`
                  )}
                </TableRow>
              ))}
              <TableRow>
                {days.map((day) => {
                  const dayProducts = range
                    ? products.filter((p) => anchorDay(p, range) === day)
                    : [];
                  if (!dayProducts.length) return <TableCell key={`add-${day}`} />;
                  return (
                    <TableCell key={`add-${day}`} align="center" sx={{ p: 0.25 }}>
                      <FormControl size="small" sx={{ minWidth: 44 }}>
                        <Select
                          value=""
                          displayEmpty
                          disabled={busy}
                          onChange={(e) => {
                            const prod = dayProducts.find((p) => String(pid(p)) === String(e.target.value));
                            if (prod) bookOne(prod);
                          }}
                          sx={{ "& .MuiSelect-select": { py: 0.25, fontSize: 12 } }}
                          renderValue={() => "+"}
                        >
                          {dayProducts.map((p) => {
                            const c = p.base ?? p.campus ?? p.residentialBase ?? "";
                            return (
                              <MenuItem key={pid(p)} value={String(pid(p))}>
                                {p.productName || p.name}
                                {c ? ` · ${c}` : ""} ({d10(p.startDate)}→{d10(p.endDate)})
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={Boolean(editBooking)} onClose={() => setEditBooking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {editBooking?.productName || "booking"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {showFinancials && (
              <TextField
                label="Charge £"
                type="number"
                size="small"
                value={editBooking?.actualCharge ?? ""}
                onChange={(e) => setEditBooking((b) => ({ ...b, actualCharge: e.target.value }))}
              />
            )}
            <TextField
              label="Start"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={editBooking?.start || ""}
              onChange={(e) => setEditBooking((b) => ({ ...b, start: e.target.value }))}
            />
            <TextField
              label="End"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={editBooking?.end || ""}
              onChange={(e) => setEditBooking((b) => ({ ...b, end: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBooking(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <BespokeEventDialog
        open={bespokeOpen}
        onClose={() => setBespokeOpen(false)}
        defaultCampus={stayRanges[0]?.campus || campusNames[0] || ""}
        defaultDate={range?.start}
        windowStart={range?.start || ""}
        windowEnd={range?.end || ""}
        onBook={bookBespoke}
      />

      <StudentCopyBookingsDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        token={token}
        selectedYear={selectedYear}
        destStudentId={studentId}
        destStays={stayRanges}
        destBlocks={blocks}
        onApplied={reload}
      />

      <Dialog open={Boolean(stayDialog)} onClose={() => setStayDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{stayDialog?.stayId ? "Edit stay" : "Add stay"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="grid-stay-campus">Campus</InputLabel>
              <Select
                labelId="grid-stay-campus"
                label="Campus"
                value={stayDialog?.campus || ""}
                onChange={(e) => setStayDialog((s) => ({ ...s, campus: e.target.value }))}
              >
                {campusNames.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Arrival"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={stayDialog?.arrivalDate || ""}
              onChange={(e) => setStayDialog((s) => ({ ...s, arrivalDate: e.target.value }))}
            />
            <TextField
              label="Departure"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={stayDialog?.departureDate || ""}
              onChange={(e) => setStayDialog((s) => ({ ...s, departureDate: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={stayDialog?.residential ?? true}
                  onChange={(e) => setStayDialog((s) => ({ ...s, residential: e.target.checked }))}
                />
              }
              label="Residential"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStayDialog(null)} disabled={staySaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveStay}
            disabled={staySaving || !stayDialog?.campus || !stayDialog?.arrivalDate || !stayDialog?.departureDate}
          >
            {staySaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
